import React from 'react';
import { Resource } from '../../constants/resources';
import { ShoppingBag, Star } from 'lucide-react';
import OptimizedImage from '../common/OptimizedImage';
import { useResourceAction } from '../../hooks/useResourceAction';

interface ResourceCardProps {
  resource: Resource;
  className?: string;
  onClick?: () => void;
  onBuyClick?: () => void;
}

const ResourceCard: React.FC<ResourceCardProps> = ({ resource, className = '', onClick, onBuyClick }) => {
  const { handleResourceAction } = useResourceAction();

  const handleBuyClick = (e: React.MouseEvent) => {
    e.preventDefault();
    handleResourceAction(resource, onBuyClick);
  };

  return (
    <div className={`group flex flex-col bg-white rounded-2xl shadow-sm hover:shadow-xl transition-all duration-300 overflow-hidden border border-contessa-100 transform hover:-translate-y-1 relative ${className}`}>
      
      {/* Badge New */}
      {resource.isNew && (
        <div className="absolute top-4 left-4 z-10 bg-contessa-800 text-white text-xs font-bold px-3 py-1 rounded-full shadow-lg flex items-center gap-1 border border-contessa-600">
          <Star size={12} fill="currentColor" />
          NUEVO
        </div>
      )}

      {/* Image Container - Clickable */}
      <div
        onClick={onClick}
        onKeyDown={(e) => {
          if (onClick && (e.key === 'Enter' || e.key === ' ')) {
            e.preventDefault();
            onClick();
          }
        }}
        role={onClick ? 'button' : undefined}
        tabIndex={onClick ? 0 : undefined}
        aria-label={onClick ? `Ver detalles de ${resource.title}` : undefined}
        className={`h-56 overflow-hidden bg-contessa-50 relative shrink-0 ${onClick ? 'cursor-pointer' : ''}`}
      >
        {resource.imageUrl ? (
          <OptimizedImage
            src={resource.imageUrl} 
            alt={resource.title} 
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
          />
        ) : (
          <div className="flex items-center justify-center h-full bg-contessa-50 text-contessa-300">
            <ShoppingBag size={48} />
          </div>
        )}
        
        {/* Overlay on hover */}
        <div className="absolute inset-0 bg-contessa-900/0 group-hover:bg-contessa-900/10 transition-colors duration-300" />
        
        <div className="absolute top-4 right-4 bg-white/95 backdrop-blur-md px-3 py-1.5 rounded-lg text-xs font-bold text-contessa-800 shadow-md border border-contessa-100">
          {resource.type.join(' + ')}
        </div>
      </div>
      
      {/* Content */}
      <div className="p-6 flex flex-col flex-grow">
        <h3
          onClick={onClick}
          onKeyDown={(e) => {
            if (onClick && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              onClick();
            }
          }}
          role={onClick ? 'button' : undefined}
          tabIndex={onClick ? 0 : undefined}
          aria-label={onClick ? `Ver detalles de ${resource.title}` : undefined}
          className={`text-lg font-bold text-contessa-950 mb-2 font-serif group-hover:text-contessa-600 transition-colors line-clamp-2 ${onClick ? 'cursor-pointer' : ''}`}
        >
          {resource.title}
        </h3>
        
        <p
          onClick={onClick}
          onKeyDown={(e) => {
            if (onClick && (e.key === 'Enter' || e.key === ' ')) {
              e.preventDefault();
              onClick();
            }
          }}
          role={onClick ? 'button' : undefined}
          tabIndex={onClick ? 0 : undefined}
          aria-label={onClick ? `Ver descripción completa de ${resource.title}` : undefined}
          className={`text-contessa-900/70 text-sm mb-6 line-clamp-3 leading-relaxed ${onClick ? 'cursor-pointer hover:text-contessa-900' : ''}`}
        >
          {resource.description}
        </p>
        
        <div className="mt-auto flex items-center justify-between pt-5 border-t border-contessa-100">
          <div className="flex flex-col justify-center">
            <div className="flex items-baseline gap-2">
              {resource.previousPriceDolars && (
                <span className="text-sm text-stone-400 line-through decoration-stone-400 px-1">
                  {resource.previousPriceDolars}
                </span>
              )}
              <span className="font-bold text-2xl text-contessa-700">
                {resource.priceDolars || 'Gratis'}
              </span>
            </div>
          </div>
          
          <button
            onClick={handleBuyClick}
            className="px-5 py-2.5 bg-contessa-800 hover:bg-contessa-700 text-white text-sm font-semibold rounded-xl transition-all shadow-md hover:shadow-lg flex items-center gap-2"
          >
            {resource.buttonText || 'Ver más'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ResourceCard;
